import { supabase } from './supabase';
import md5 from 'js-md5';

export interface PayHereOrderRequest {
  itemId: string;
  itemName: string;
  originalAmount: number;
  currency: string;
  feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage: number;
  gatewayFeePercentage: number;
  registryId: string;
  registryOwnerId: string;
  payerFirstName: string;
  payerLastName: string;
  payerEmail: string;
  payerPhone: string;
  payerAddress: string;
  payerCity: string;
  guestId?: string;
  contributionMessage?: string;
  isGroupGift?: boolean;
  isMoneyGift?: boolean;
}

export function calculateFinalAmount(
  originalAmount: number,
  feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT',
  platformFeePercentage: number,
  gatewayFeePercentage: number
): { finalAmount: number; platformFee: number; gatewayFee: number; totalFees: number } {
  const platformFeeRate = platformFeePercentage / 100;
  const gatewayFeeRate = gatewayFeePercentage / 100;
  const platformFee = originalAmount * platformFeeRate;
  const gatewayFee = originalAmount * gatewayFeeRate;
  const totalFees = platformFee + gatewayFee;

  let finalAmount = originalAmount;
  switch (feeResponsibility) {
    case 'GUEST':
      finalAmount = originalAmount + totalFees;
      break;
    case 'COUPLE':
      finalAmount = originalAmount;
      break;
    case 'SPLIT':
      finalAmount = originalAmount + (totalFees / 2);
      break;
  }

  return {
    finalAmount: Number(finalAmount.toFixed(2)),
    platformFee: Number(platformFee.toFixed(2)),
    gatewayFee: Number(gatewayFee.toFixed(2)),
    totalFees: Number(totalFees.toFixed(2))
  };
}

export async function createPayHereOrder(request: PayHereOrderRequest): Promise<{ orderId: string; hash: string; finalAmount: number; merchantId: string }> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || supabaseAnonKey;

  const response = await fetch(`${supabaseUrl}/functions/v1/payhere-create-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create payment order');
  }

  return response.json();
}

export function submitPayHereCheckout(params: {
  merchantId: string;
  orderId: string;
  hash: string;
  itemName: string;
  finalAmount: number;
  currency: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  registryId?: string;
}) {
  const merchantId = params.merchantId;
  const isSandbox = import.meta.env.VITE_PAYHERE_SANDBOX === 'true';
  const actionUrl = isSandbox
    ? 'https://sandbox.payhere.lk/pay/checkout'
    : 'https://www.payhere.lk/pay/checkout';

  const baseUrl = window.location.origin;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionUrl;
  form.style.display = 'none';

  const fields: Record<string, string> = {
    merchant_id: merchantId,
    return_url: `${baseUrl}/payment/success`,
    cancel_url: `${baseUrl}/payment/cancel`,
    notify_url: `${supabaseUrl}/functions/v1/payhere-notify`,
    order_id: params.orderId,
    items: params.itemName,
    currency: params.currency,
    amount: params.finalAmount.toFixed(2),
    first_name: params.firstName,
    last_name: params.lastName,
    email: params.email,
    phone: params.phone,
    address: params.address,
    city: params.city,
    country: 'Sri Lanka',
    hash: params.hash,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  if (params.registryId) {
    try { sessionStorage.setItem('payhere_registry_id', params.registryId); } catch {}
  }

  document.body.appendChild(form);
  form.submit();
}

export async function loadFeeSettings(): Promise<{ platformFee: number; gatewayFee: number }> {
  const { data: platformFeeData } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'platform_fee_rate')
    .maybeSingle();

  const { data: gatewayFeeData } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'gateway_fee_rate')
    .maybeSingle();

  return {
    platformFee: parseFloat(platformFeeData?.value || '0'),
    gatewayFee: parseFloat(gatewayFeeData?.value || '0')
  };
}
